import React, { useState, useRef } from 'react';
import { X, Receipt } from 'lucide-react';
import { DialogShell } from './ui/DialogShell';
import { useLumisial } from '../context/LumisialContext';
import { movimientoService } from '../services/movimientoService';
import { useGastoForm, type GastoFormState } from '../hooks/useGastoForm';
import { GastoFormContent } from './gastos/GastoFormContent';
import { useMiembroSearch } from '../hooks/useMiembroSearch';
import { useMiembroSearchJA } from '../hooks/useMiembroSearchJA';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { OwnerContext, buildOwnerPayload } from '../types/actividad';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { mensajeErrorDeNegocio } from '../utils/erroresDeNegocio';
import { ComprobanteInput } from './comprobantes/ComprobanteInput';
import { MedioPagoFolio } from './comprobantes/MedioPagoFolio';
import { useComprobanteFormulario } from '../hooks/useComprobanteFormulario';
import type { EntidadDinero } from '../types/comprobante';

interface GastoModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  readonly ownerContext?: OwnerContext; // JAD/JAN mode cuando se provee; JAL mode si ausente
}

type OwnerPayload = ReturnType<typeof buildOwnerPayload> | { readonly lumisial_id: string };

function armarGasto(form: GastoFormState, ownerPayload: OwnerPayload) {
  const subcategoriaFinal = form.subcategoria === 'Otra' ? form.otraSubcategoria.trim() : form.subcategoria;
  return {
    fecha: form.fecha,
    tipo: 'egreso' as const,
    monto: Number.parseFloat(form.monto),
    descripcion: form.descripcion.trim(),
    ...ownerPayload,
    categoria_egreso: form.categoriaEgreso,
    subcategoria_egreso: subcategoriaFinal,
    requiere_reembolso: form.esDonacion ? false : form.requiereReembolso,
    reembolsado: false,
    reembolso_a_miembro_id: !form.esDonacion && form.requiereReembolso && form.reembolsoTipo === 'miembro' ? form.miembroReembolsoId : undefined,
    reembolso_a_nombre_externo: !form.esDonacion && form.requiereReembolso && form.reembolsoTipo === 'externo' ? form.nombreExternoReembolso.trim() : undefined,
    afecta_balance: !form.esDonacion,
    donacion_de_miembro_id: form.esDonacion && form.donanteTipo === 'miembro' && form.miembroDonante ? form.miembroDonante : undefined,
    donacion_de_nombre_externo: form.esDonacion && form.donanteTipo === 'externo' && form.nombreExternoDonante.trim() ? form.nombreExternoDonante.trim() : undefined,
    anulado: false,
  };
}

function entidadDelGasto(
  ownerContext: OwnerContext | undefined,
  lumisial: { readonly id: string; readonly nombre: string } | null,
): EntidadDinero | null {
  if (ownerContext) {
    return ownerContext.tipo === 'global' ? null : { ...ownerContext, tipo: ownerContext.tipo };
  }
  return lumisial ? { tipo: 'lumisial', id: lumisial.id, nombre: lumisial.nombre } : null;
}

// Cada apertura monta un formulario nuevo con el día civil actual de Chile.
export const GastoModal: React.FC<GastoModalProps> = (props) =>
  props.isOpen ? <FormularioGasto {...props} /> : null;

const FormularioGasto: React.FC<GastoModalProps> = ({ isOpen, onClose, onSuccess, ownerContext }) => {
  const { selectedLumisial } = useLumisial();
  const [submitting, setSubmitting] = useState(false);

  // Ambos hooks llamados siempre (no condicional); solo se usa uno según el modo
  const { handleSearch: searchJAL } = useMiembroSearch(ownerContext ? null : (selectedLumisial?.id || null));
  const { handleSearch: searchJADJAN } = useMiembroSearchJA(ownerContext ?? null);
  const handleSearchMiembros = ownerContext ? searchJADJAN : searchJAL;

  const { error, clearError, handleError } = useErrorHandler();
  const form = useGastoForm();
  // Una donación no sale de la caja: el comprobante y el medio de pago son opcionales.
  const comprobante = useComprobanteFormulario({
    entidad: entidadDelGasto(ownerContext, selectedLumisial),
    fechaMovimiento: form.fecha,
    permiteSubirDespues: true,
    pideMedioPago: !form.esDonacion,
    sentidoDinero: 'egreso',
    obligatoriedad: form.esDonacion ? 'nunca' : 'desde_vigencia',
  });

  // "Subir después" no existe para una donación: si estaba elegido, se descarta al marcarla.
  const alternarDonacion = () => {
    if (!form.esDonacion && comprobante.seleccion.tipo === 'despues') comprobante.cambiarSeleccion({ tipo: 'ninguno' });
    form.handleDonacionToggle();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!ownerContext && !selectedLumisial) return;

    const validationError = form.validateForm();
    if (validationError) { handleError(validationError); return; }
    const errorComprobante = comprobante.validar();
    if (errorComprobante) { handleError(errorComprobante); return; }

    try {
      setSubmitting(true);
      const respaldo = await comprobante.resolver();
      const ownerPayload = ownerContext
        ? buildOwnerPayload(ownerContext)
        : { lumisial_id: selectedLumisial!.id };

      await movimientoService.create({ ...armarGasto(form, ownerPayload), ...respaldo });

      form.resetForm();
      comprobante.reiniciar();
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const deNegocio = mensajeErrorDeNegocio(err);
      if (deNegocio) handleError(deNegocio);
      else reportarErrorInesperado(err, 'Error al registrar el gasto. Por favor intenta nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => { form.resetForm(); comprobante.reiniciar(); onClose(); };
  const hasUnsavedChanges = submitting || error !== '' || form.hasFormChanges() || comprobante.hayCambios;
  const closeRef = useRef<(() => void) | null>(null);
  const requestClose = () => closeRef.current?.();

  if (!isOpen) return null;

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={handleClose}
      size="3xl"
      hasUnsavedChanges={hasUnsavedChanges}
      closeRef={closeRef}
      header={(
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-orange-100 p-2 rounded-lg">
              <Receipt className="w-6 h-6 text-orange-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800">Registrar Gasto</h2>
          </div>
          <button type="button" onClick={requestClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" aria-label="Cerrar">
            <X className="w-6 h-6 text-slate-600" />
          </button>
        </div>
      )}
    >
        <form onSubmit={handleSubmit} className="space-y-6">
          <GastoFormContent
            {...form}
            handleDonacionToggle={alternarDonacion}
            error={error}
            onClearError={clearError}
            onSearchMiembros={handleSearchMiembros}
            bloqueComprobante={(
              <ComprobanteInput comprobante={comprobante} deshabilitado={submitting} />
            )}
            bloqueMedioPago={<MedioPagoFolio comprobante={comprobante} deshabilitado={submitting} />}
          />

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={requestClose}
              className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 transition-colors"
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={submitting || comprobante.subiendo}
            >
              {submitting ? 'Guardando...' : 'Registrar Gasto'}
            </button>
          </div>
        </form>
    </DialogShell>
  );
};
